/**
 * Canonical error hierarchy for the ApiClient.
 *
 * Mirrors the v1 ``APIError`` / ``RateLimitError`` shapes (ando-frontend
 * ``app/api/apiFetch.ts`` + ``app/types/apiError.ts``) so callers can do
 * ``err instanceof ApiError`` regardless of which sub-class was thrown.
 *
 * Backend ``AppError`` (FastAPI ``src/api/error_handler.py``) emits responses
 * shaped as ``AppErrorPayload`` — the type guards re-exported from this
 * module narrow common cases without repeating
 * ``err.data?.error_code === "..."`` casts at call-sites.
 */

/**
 * Standard API error.
 *
 * ``data`` carries the parsed JSON body when the response was JSON. For
 * backend ``AppError`` responses it conforms to ``AppErrorPayload``.
 */
export class ApiError extends Error {
  status: number;
  response?: Response;
  data?: unknown;

  constructor(message: string, status: number, response?: Response, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.response = response;
    this.data = data;
  }
}

/** Thrown when the response is HTTP 429 with a ``Retry-After`` header. */
export class RateLimitError extends ApiError {
  retryAfter: number;

  constructor(message: string, retryAfter: number, response?: Response, data?: unknown) {
    super(message, 429, response, data);
    this.name = "RateLimitError";
    this.retryAfter = retryAfter;
  }
}

/** Thrown when the response is HTTP 401 and refresh-and-retry has failed. */
export class AuthError extends ApiError {
  constructor(message = "Unauthorized", response?: Response, data?: unknown) {
    super(message, 401, response, data);
    this.name = "AuthError";
  }
}

/** Thrown for transport-level failures (timeout, DNS, offline, abort). */
export class NetworkError extends ApiError {
  override cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message, 0);
    this.name = "NetworkError";
    this.cause = cause;
  }
}

/**
 * Thrown when the response body is a 2xx but fails zod schema validation.
 *
 * Carries the raw zod ``issues`` array so the caller can render field-level
 * messages or log structured diagnostics. The HTTP transport itself
 * succeeded — `response` is set; only the contract was wrong.
 */
export class SchemaError extends ApiError {
  /** ``ZodIssue[]`` (kept as ``unknown[]`` to avoid a zod dependency in core/api). */
  issues: unknown[];

  constructor(message: string, issues: unknown[], response?: Response, data?: unknown) {
    super(message, response?.status ?? 200, response, data);
    this.name = "SchemaError";
    this.issues = issues;
  }
}

/** Backend AppError JSON shape (see ``src/api/error_handler.py``). */
export interface AppErrorPayload {
  success: false;
  error: string;
  detail?: string;
  error_code?: string;
  details?: Record<string, unknown>;
  timestamp?: string;
}

function asPayload(data: unknown): AppErrorPayload | null {
  if (data && typeof data === "object" && "error_code" in data) {
    return data as AppErrorPayload;
  }
  return null;
}

/** True when the error is a 409 with ``error_code === "REAUTH_REQUIRED"``. */
export function isReauthRequired(err: unknown): err is ApiError {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  return asPayload(err.data)?.error_code === "REAUTH_REQUIRED";
}

/** True when the error is a 409 active-job-exists conflict. */
export function isJobConflict(err: unknown): err is ApiError {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const data = err.data;
  if (!data || typeof data !== "object") return false;
  const obj = data as Record<string, unknown>;
  if (obj.error === "active_job_exists" || typeof obj.active_job_id === "string") {
    return true;
  }
  return asPayload(data)?.error_code === "ACTIVE_JOB_EXISTS";
}

/** True when the error signals a plan/quota limit (402 or 429 with explicit code). */
export function isPlanLimit(err: unknown): err is ApiError {
  if (!(err instanceof ApiError)) return false;
  if (err.status !== 402 && err.status !== 429) return false;
  const code = asPayload(err.data)?.error_code;
  return code === "QUOTA_EXCEEDED" || code === "PLAN_LIMIT_EXCEEDED" || code === "RATE_LIMIT_EXCEEDED";
}

/**
 * Pull a user-presentable message out of any error shape. Used by every
 * mutation ``onError`` callback in the SPA — DRY across 20+ call sites.
 *
 * Priority: ``Error.message`` → BE-shaped ``{ message?: string }`` →
 * the caller-provided fallback (already-translated string).
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string" && m.length > 0) return m;
  }
  return fallback;
}
